import { ArticleList } from '@/components/ArticleList'
import { DocsLayout } from '@/components/DocsLayout'
import { getCategoryLabel, getCategoryMeta } from '@/lib/categories'
import { SITE_NAME } from '@/lib/config'
import { useHelpIndex } from '@/lib/data'
import { useHead } from '@/lib/head'
import { CaretRightIcon } from '@phosphor-icons/react'
import { Link, useParams } from 'react-router-dom'

import NotFoundPage from './NotFound'

const TYPE_ORDER = ['workflow', 'concept', 'reference']
const TYPE_HEADINGS: Record<string, string> = {
  workflow: 'How-to guides',
  concept: 'Concepts',
  reference: 'Reference',
}

export default function CategoryPage() {
  const { slug = '' } = useParams()
  const { index } = useHelpIndex()
  const meta = getCategoryMeta(slug)
  const label = getCategoryLabel(slug)

  useHead({
    title: `${label} — ${SITE_NAME}`,
    description: meta.description,
    path: `/categories/${encodeURIComponent(slug)}`,
  })

  if (index && !index.categories.some(row => row.slug === slug)) {
    return <NotFoundPage />
  }

  const articles = (index?.articles ?? [])
    .filter(article => article.moduleSlug === slug)
    .sort((a, b) => a.title.localeCompare(b.title))
  const groups = TYPE_ORDER.map(type => ({
    type,
    heading: TYPE_HEADINGS[type],
    articles: articles.filter(article => article.type === type),
  })).filter(group => group.articles.length > 0)
  const otherTypes = articles.filter(
    article => !TYPE_ORDER.includes(article.type),
  )
  const Icon = meta.icon

  return (
    <DocsLayout activeCategorySlug={slug}>
      <div className="page">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/">Help Center</Link>
          <CaretRightIcon size={11} />
          <span>{label}</span>
        </nav>
        <header className={`category-head hue-${meta.hue}`}>
          <span className="category-icon large">
            <Icon size={26} weight="duotone" />
          </span>
          <div>
            <h1>{label}</h1>
            <p>{meta.description}</p>
          </div>
        </header>

        {!index ? (
          <div className="list-skeleton">
            {Array.from({ length: 6 }).map((_, position) => (
              <span key={position} />
            ))}
          </div>
        ) : (
          <>
            {groups.map(group => (
              <section key={group.type} className="category-section">
                <h2>
                  {group.heading}{' '}
                  <span className="count">{group.articles.length}</span>
                </h2>
                <ArticleList articles={group.articles} />
              </section>
            ))}
            {otherTypes.length > 0 ? (
              <section className="category-section">
                <h2>More</h2>
                <ArticleList articles={otherTypes} />
              </section>
            ) : null}
          </>
        )}
      </div>
    </DocsLayout>
  )
}
